import { Help } from '@carbon/react/icons';
import {
  Checkbox,
  InlineLoading,
  InlineNotification,
  Modal,
} from '@carbon/react';
import { FloatingTooltip } from 'components/floating-tooltip';
import { Toggle } from 'components/toggle/toggle';
import { useAtom } from 'jotai';
import { isNil } from 'lodash-es';
import { formatMutationResource } from 'utils/misc';
import { format0 } from 'utils/number';
import {
  LandingAnalysisDefaultParametersResponse,
  WellLandingAnalysisRequestResponse,
} from 'types/api-responses';
import {
  enableAutoUpdateAtom,
  triggerWellLandingAnalysisAtom,
  useWellLandingPreviewAtom,
} from '../atoms';
import style from './process-well-landings-modal.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProcessWellLandingsModalProps {
  isOpen: boolean;
  collectionId: string;
  collectionName: string;
  landingAutoUpdateEnabled: boolean;
  autoUpdateEnabled: boolean;
  onAutoUpdateChange: (value: boolean) => void;
  onClose: () => void;
  onConfirm: (response: WellLandingAnalysisRequestResponse) => void;
  landingParameters?: LandingAnalysisDefaultParametersResponse['landingParameters'];
  hasParametersError?: boolean;
  collectionValidity?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ProcessWellLandingsModal = ({
  isOpen,
  collectionId,
  collectionName,
  landingAutoUpdateEnabled,
  autoUpdateEnabled,
  onAutoUpdateChange,
  onClose,
  onConfirm,
  landingParameters,
  hasParametersError,
  collectionValidity,
}: ProcessWellLandingsModalProps) => {
  const wellLandingPreviewAtom = useWellLandingPreviewAtom(
    isOpen
      ? `/structure_grid_collections/${collectionId}/well_landing_preview`
      : undefined
  );
  const [wellLandingPreview] = useAtom(wellLandingPreviewAtom);
  const isLoadingPreview = wellLandingPreview.loading;
  const hasPreviewError =
    !wellLandingPreview.loading &&
    !isNil(wellLandingPreview.data) &&
    'error' in wellLandingPreview.data;
  const wellLandingPreviewData =
    wellLandingPreview.data && !('error' in wellLandingPreview.data)
      ? wellLandingPreview.data
      : null;

  const [enableAutoUpdateState, enableAutoUpdate] =
    useAtom(enableAutoUpdateAtom);
  const [triggerState, triggerWellLandingAnalysis] = useAtom(
    triggerWellLandingAnalysisAtom
  );
  const { loading: isTriggerSubmitting } = formatMutationResource(triggerState);
  const { loading: isPatchSubmitting } = formatMutationResource(
    enableAutoUpdateState
  );
  const isSubmitting = isTriggerSubmitting || isPatchSubmitting;
  const hasSubmitError =
    triggerState.result?.type === 'error' ||
    triggerState.result?.type === 'fatal';
  const hasAutoUpdateError =
    enableAutoUpdateState.result?.type === 'error' ||
    enableAutoUpdateState.result?.type === 'fatal';

  const handleConfirm = () => {
    triggerWellLandingAnalysis({
      url: '/well_landing_analysis_requests',
      type: 'POST',
      data: {
        structureGridCollectionId: Number(collectionId),
        scope: 'collection',
        landingParameters: {
          usePerforations: landingParameters?.usePerforations ?? false,
          stepLengthFt: landingParameters?.stepLengthFt ?? 0,
          horizontalInclinationCutoffDeg:
            landingParameters?.horizontalInclinationCutoffDeg ?? 0,
          thicknessCutoffFt: landingParameters?.thicknessCutoffFt ?? 0,
        },
        config: {
          notifyProgress: true,
          notifyIndividualCompletions: false,
        },
      },
      onSuccess: (data) => {
        if (autoUpdateEnabled !== landingAutoUpdateEnabled) {
          enableAutoUpdate({
            url: `/structure_grid_collections/${collectionId}`,
            type: 'PATCH',
            data: { landingAutoUpdateEnabled: autoUpdateEnabled },
            onSuccess: () => onConfirm(data),
          });
        } else {
          onConfirm(data);
        }
      },
    });
  };

  const isDisabled = isLoadingPreview || isSubmitting;
  const formattedWellCount = format0(
    wellLandingPreviewData?.candidateWellCount ?? 0
  );

  return (
    <Modal
      open={isOpen}
      modalLabel={collectionName}
      modalHeading="Process Well Landings"
      primaryButtonText={isSubmitting ? 'Processing...' : 'Confirm'}
      primaryButtonDisabled={isDisabled}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleConfirm}
      onRequestClose={
        isDisabled
          ? undefined
          : () => {
              onAutoUpdateChange(landingAutoUpdateEnabled);
              onClose();
            }
      }
      className={style.modal}
    >
      {hasSubmitError && (
        <InlineNotification
          kind="error"
          title="Failed to start well landing analysis."
          subtitle="Please try again."
          hideCloseButton
          lowContrast
        />
      )}
      {hasAutoUpdateError && (
        <InlineNotification
          kind="error"
          title="Failed to update Auto Update setting."
          subtitle="Please try again."
          hideCloseButton
          lowContrast
        />
      )}
      {hasPreviewError && (
        <InlineNotification
          kind="error"
          title="Failed to load preview."
          subtitle="Unable to retrieve well count. Please close and try again."
          hideCloseButton
          lowContrast
        />
      )}
      {hasParametersError && (
        <InlineNotification
          kind="error"
          title="Failed to load landing parameters."
          subtitle="Parameter values are unavailable. Please close and try again."
          hideCloseButton
          lowContrast
        />
      )}
      <div className={style.body}>
        {isLoadingPreview && (
          <div className={style.previewLoadingOverlay}>
            <InlineLoading description="Loading preview…" status="active" />
          </div>
        )}

        <p className={style.description}>
          This process will run landing for{' '}
          <strong>{formattedWellCount} wells</strong>. A large number of wells
          can have extended processing time.
        </p>

        <p className={style.proceed}>Proceed with processing?</p>

        <div className={style.toggleRow}>
          <Toggle
            isActive={autoUpdateEnabled}
            label="Auto Update"
            ariaLabel="Auto Update"
            handleToggle={onAutoUpdateChange}
            disabled={collectionValidity === 'invalid'}
          />
          <FloatingTooltip
            content="Automatically runs Landing Analysis for new wells or updated Wellbore Geometry within the collection. Auto Update and Process Well Landings are disabled when the Collection is invalid."
            placement="right"
          >
            <Help className={style.infoIcon} />
          </FloatingTooltip>
        </div>

        <div className={style.fields}>
          <div className={style.fieldFull}>
            <span className={style.fieldLabel}>Structure Grid Collection</span>
            <span className={style.fieldValue}>{collectionName}</span>
          </div>

          <div className={style.fieldGrid}>
            <div className={style.fieldItem}>
              <span className={style.fieldLabel}>Inclination Cutoff</span>
              <span className={style.fieldValue}>
                {landingParameters?.horizontalInclinationCutoffDeg ?? '—'}
              </span>
            </div>
            <div className={style.fieldItem}>
              <span className={style.fieldLabel}>
                Well Segment Step Frequency
              </span>
              <span className={style.fieldValue}>
                {landingParameters?.stepLengthFt ?? '—'}
              </span>
            </div>
            <div className={style.fieldItem}>
              <span className={style.fieldLabel}>Maximum Thickness</span>
              <span className={style.fieldValue}>
                {landingParameters?.thicknessCutoffFt !== null &&
                landingParameters?.thicknessCutoffFt !== undefined
                  ? `${format0(landingParameters.thicknessCutoffFt)}`
                  : '—'}
              </span>
            </div>
            <div className={style.fieldItem}>
              <Checkbox
                id="use-perforation-geometry"
                labelText="Use Perforation Geometry"
                checked={landingParameters?.usePerforations ?? false}
                disabled
                onChange={() => {}}
                className={style.checkbox}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
